import { ReactNode, useState, useCallback, createContext, useContext } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from './LanguageSwitcher'
import UserMenu from './UserMenu'
import CookieBanner from './CookieBanner'

interface LayoutProps {
  children: ReactNode
}

// Announce context — used by pages to announce messages to screen readers
const AnnounceContext = createContext<(message: string) => void>(() => {})
export const useAnnounce = () => useContext(AnnounceContext)

export default function Layout({ children }: LayoutProps) {
  const { t, i18n } = useTranslation()
  const [announcement, setAnnouncement] = useState('')

  const announce = useCallback((message: string) => {
    // Clear first to re-trigger announcement for same message
    setAnnouncement('')
    requestAnimationFrame(() => setAnnouncement(message))
  }, [])

  return (
    <AnnounceContext.Provider value={announce}>
      <div className="layout">
        <a href="#main-content" className="skip-link">
          {t('common.skipToContent')}
        </a>
        <header className="app-header">
          <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 2rem', flexWrap: 'nowrap' }}>
            <Link to={`/${i18n.language}`} aria-label={t('common.goHome')}>
              <img
                src="/logo_v3.svg"
                alt="Sign This!"
                className="app-logo"
                style={{ height: '4.5rem', width: 'auto', flexShrink: 0, objectFit: 'contain' }}
              />
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'nowrap', flexShrink: 0 }}>
              <LanguageSwitcher />
              <UserMenu />
            </div>
          </div>
        </header>
        <main id="main-content">{children}</main>
        <footer className="app-footer" role="contentinfo">
          <div className="container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem 2rem', flexWrap: 'wrap' }}>
            <span>{t('footer.poweredBy')}</span>
            <span>·</span>
            <a href="mailto:contact@loxia.ai">{t('footer.contact')}: contact@loxia.ai</a>
            <span>·</span>
            <a href="mailto:contact@loxia.ai">{t('footer.accessibilityContact')}</a>
          </div>
        </footer>
        <CookieBanner />
        {/* Live region for screen reader announcements */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {announcement}
        </div>
      </div>
    </AnnounceContext.Provider>
  )
}
